'use client';

import { ComponentPropsWithoutRef, ComponentRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';
import styles from './Tabs.module.css';

type TabsVariant = 'default' | 'pills';

/**
 * Radix's Root writes dir="ltr" on itself unless it is told otherwise, which in a right-to-left
 * app puts the tab strip - and the arrow keys that move between tabs - the wrong way round.
 * All sixteen call sites were working around that with an inline direction, half of them with a
 * hardcoded 'rtl' that then reads backwards in English. The direction is taken from the
 * language instead, by the same rule i18n uses to set it on the document, and an explicit dir
 * prop still wins for anyone who needs one.
 */
const Tabs = forwardRef<ComponentRef<typeof Root>, ComponentPropsWithoutRef<typeof Root>>(
  ({ dir, ...props }, ref) => {
    const { i18n } = useTranslation();
    return <Root ref={ref} dir={dir ?? (i18n.language === 'he' ? 'rtl' : 'ltr')} {...props} />;
  }
);
Tabs.displayName = 'Tabs';

type TabsListProps = ComponentPropsWithoutRef<typeof List> & {
  variant?: TabsVariant;
};

const TabsList = forwardRef<ComponentRef<typeof List>, TabsListProps>(({ className, variant = 'default', ...props }, ref) => (
  <List
    ref={ref}
    // className={`inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground${className}`}
    className={`${variant === 'pills' ? styles.listPills : styles.list} ${className ?? ''}`}
    {...props}
  />
));
TabsList.displayName = List.displayName;

type TabsTriggerProps = ComponentPropsWithoutRef<typeof Trigger> & {
  variant?: TabsVariant;
};

const TabsTrigger = forwardRef<ComponentRef<typeof Trigger>, TabsTriggerProps>(({ className, children, variant = 'default', ...props }, ref) => (
  <Trigger
    ref={ref}
    //className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-red-500 data-[state=active]:text-foreground data-[state=active]:shadow${className}`}
    className={`${variant === 'pills' ? styles.triggerPills : styles.trigger} ${variant === 'default' ? 'body-lr-bold' : ''} ${className ?? ''}`}
    {...props}
  >
    <span className={variant === 'pills' ? styles['trigger-text-pills'] : styles['trigger-text']}>{children}</span>
    {variant === 'default' && <span className={styles['trigger-bottom']}></span>}
  </Trigger>
));
TabsTrigger.displayName = Trigger.displayName;

const TabsContent = forwardRef<ComponentRef<typeof Content>, ComponentPropsWithoutRef<typeof Content>>(({ className, ...props }, ref) => (
  <Content
    ref={ref}
    //className={`mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2${className}`}
    className={`${styles.content} ${className}`}
    {...props}
  />
));
TabsContent.displayName = Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
